import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  LendingDisbursementPaymentOption,
  LendingPaymentOptionDetails,
  LendingPaymentSource,
  LendingPaymentSourceDisplayGroup,
  VXLIconResultTypenames,
} from '@varo/graphql-schema-types';
import { formatAccountName } from 'features/advance/screens/registration/AutoPaymentScreen/AdvanceAccountListBottomSheet.helpers';
import React, { Dispatch, forwardRef, RefObject, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLeadingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemLeadingAccessory';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isWeb } from 'shared/constants/platform';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { PlaidOpenResult } from 'shared/hooks/usePlaidV2/types';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

export interface AdvanceAccountListBottomSheetProps {
  setSelectedAccount: (account: LendingPaymentOptionDetails) => void;
  fetching: boolean;
  accountGroups: LendingDisbursementPaymentOption[];
  showAddPlaid: boolean;
  setOpenPlaid: () => Promise<PlaidOpenResult | undefined>;
  showAddDebitCard: boolean;
  setOpenAddDebitCard: Dispatch<SetStateAction<boolean>>;
  entryPoint: 'manualPay' | 'autoPay';
}

const leadingAccessory = (
  account: LendingPaymentOptionDetails,
): ListItemLeadingAccessoryProps => ({
  id: account.name,
  name: VXLComponentId.ICON_AVATAR,
  props: {
    variant: 'MD',
    iconKey:
      account.icon?.__typename === VXLIconResultTypenames.VXLIconType
        ? account.icon.key
        : undefined,
    imageSource: {
      uri:
        account.icon?.__typename === VXLIconResultTypenames.URLType
          ? account.icon.url
          : undefined,
    },
  },
});

const AdvanceAccountListBottomSheet = (
  {
    setSelectedAccount,
    fetching,
    accountGroups,
    showAddPlaid,
    setOpenPlaid,
    showAddDebitCard,
    setOpenAddDebitCard,
    entryPoint,
  }: AdvanceAccountListBottomSheetProps,
  bottomSheetRef: RefObject<BottomSheetModal>,
) => {
  const { t } = useTranslation('moveMoney', { keyPrefix: 'transferStrings' });
  const { isFeatureEnabled } = useStatsig();
  // TODO: JT-141819 normalize FF advance_manualpay_ach
  const isVaManualPayAchEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_VA_MANUAL_PAY_ACH,
  );

  const entryPointIsManualPay = entryPoint === 'manualPay';
  const entryPointIsAutoPay = entryPoint === 'autoPay';

  const showAddPlaidLink =
    showAddPlaid &&
    ((isVaManualPayAchEnabled && entryPointIsManualPay) || entryPointIsAutoPay);

  const showAddDebitCardLink =
    showAddDebitCard && (entryPointIsManualPay || entryPointIsAutoPay);

  const dismiss = () => bottomSheetRef?.current?.dismiss();

  const selectAccount = (a: LendingPaymentOptionDetails) => {
    setSelectedAccount(a);
    dismiss();
  };

  const linkAcct = () => {
    void setOpenPlaid();
  };

  const linkCard = () => {
    setOpenAddDebitCard(true);
    dismiss();
  };

  return (
    <BottomSheet
      ref={bottomSheetRef}
      accessibilityLabel="Payment method selection"
    >
      <View style={styles.bottomSheetContainer}>
        <TitleMdText style={styles.title} numberOfLines={1}>
          {t('paymentFrom')}
        </TitleMdText>

        {accountGroups.map((g) => {
          const details = g.details || [];
          let label: string;
          switch (g.displayGroup) {
            case LendingPaymentSourceDisplayGroup.Ach:
              if (isVaManualPayAchEnabled && entryPointIsManualPay) {
                label = t('externalAccounts');
                break;
              }
              label = '';
              break;
            case LendingPaymentSourceDisplayGroup.Internal:
              label = t('varoAccounts');
              break;
            case LendingPaymentSourceDisplayGroup.DebitCard:
              if (entryPointIsManualPay) {
                label = t('externalDebitCards');
                break;
              }
              label = '';
              break;
          }
          return (
            <React.Fragment key={g.displayGroup}>
              {details.length > 0 && label ? (
                <BodySmBoldText
                  data-test-id="body-text"
                  numberOfLines={1}
                  style={styles.sectionTitle}
                >
                  {label}
                </BodySmBoldText>
              ) : null}
              {fetching &&
              g.displayGroup === LendingPaymentSourceDisplayGroup.Ach ? (
                <View style={styles.loader}>
                  <LoadingSpinner />
                </View>
              ) : (
                details
                  .filter((a) => {
                    // filter out ach based on FF
                    if (
                      a.source === LendingPaymentSource.ExternalAch &&
                      !isVaManualPayAchEnabled &&
                      entryPointIsManualPay
                    ) {
                      return undefined;
                    }
                    return a;
                  })
                  .map((a, index, arr) => {
                    let secondary: string | null = null;
                    if (a?.network) {
                      secondary = a.network;
                    } else if (a?.balance?.formatted) {
                      secondary = `${t('available')} ${a.balance.formatted}`;
                    }

                    const isLast = index === arr.length - 1;
                    const isInternalAccount =
                      a.source === LendingPaymentSource.InternalSavings ||
                      a.source === LendingPaymentSource.InternalDda;
                    const isExternalDebitCard =
                      a.source === LendingPaymentSource.ExternalDebitCard;
                    const showDivider = !(
                      (isInternalAccount && isLast) ||
                      isExternalDebitCard
                    );

                    return (
                      <View key={a.accountId}>
                        <ListItem
                          primaryCopy={formatAccountName(
                            a.name || '',
                            a.maskedAccountNumber || '',
                          )}
                          secondaryCopy={secondary}
                          showsChevron={false}
                          onPress={() => selectAccount(a)}
                          leadingAccessory={leadingAccessory(a)}
                          isDisabled={false}
                          analyticsId="paymentAccountSelected"
                          data-testId={`bottom-sheet-account-select-${a.name}`}
                          customAttributes={{
                            account_type: a.source?.toLowerCase(),
                            institution_name: a.name?.toLowerCase(),
                          }}
                        />
                        {showDivider && (
                          <View style={styles.divider}>
                            <HairLineDivider />
                          </View>
                        )}
                      </View>
                    );
                  })
              )}
            </React.Fragment>
          );
        })}

        {showAddPlaidLink ? (
          <ListItem
            primaryCopy={t('linkAnAccount')}
            onPress={linkAcct}
            leadingAccessory={{
              id: 'link-account',
              name: VXLComponentId.ICON,
              props: {
                iconSize: 'MD',
                iconKey: 'MONEY',
                iconAccessibilityLabel: 'money-icon',
              },
            }}
            isDisabled={false}
            analyticsId="linkExternalAccountClicked"
            customAttributes={{ cta: 'Link a bank account' }}
          />
        ) : null}

        {showAddDebitCardLink ? (
          <ListItem
            primaryCopy={t('linkADebitCard')}
            onPress={linkCard}
            leadingAccessory={{
              id: 'link-debit-card',
              name: VXLComponentId.ICON,
              props: {
                iconSize: 'MD',
                iconKey: 'MONEY',
                iconAccessibilityLabel: 'money-icon',
              },
            }}
            isDisabled={false}
            analyticsId="linkDebitCardClicked"
            customAttributes={{ cta: 'Link a debit card' }}
          />
        ) : null}
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  bottomSheetContainer: { marginBottom: isWeb ? theme.spacing.m : 0 },
  divider: { paddingHorizontal: theme.spacing.m },
  loader: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    paddingVertical: theme.spacing.m,
  },
  sectionTitle: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  title: { paddingHorizontal: theme.spacing.m },
});

export default forwardRef(AdvanceAccountListBottomSheet);
